import { createApi } from '@reduxjs/toolkit/query/react';
import { appPortalBaseQuery } from '@/features/baseQuery.ts';
import type {
  BackendDataMaskingActiveRunDto,
  BackendDataMaskingRunPageDto,
  BackendDataMaskingRunSummaryDto,
  DataMaskingActiveRun,
  DataMaskingRunPage,
  DataMaskingRunSummary,
  GetDataMaskingRunsRequest,
} from '@/features/data-masking/data-masking.def.ts';
import {
  transformDataMaskingActiveRun,
  transformDataMaskingRunPage,
  transformDataMaskingRunSummary,
} from '@/features/data-masking/data-masking.mappers.ts';

/** Read-only client for the data-masking control API. */
export const dataMaskingApi = createApi({
  reducerPath: 'dataMaskingApi',
  baseQuery: appPortalBaseQuery(),
  tagTypes: ['DataMaskingRun'],
  endpoints: (builder) => ({
    getDataMaskingRuns: builder.query<DataMaskingRunPage, GetDataMaskingRunsRequest | void>({
      query: (params) => ({
        url: '/operations/data-masking/runs',
        method: 'GET',
        params: params ?? undefined,
      }),
      transformResponse: (response: BackendDataMaskingRunPageDto) =>
        transformDataMaskingRunPage(response),
      providesTags: (result) =>
        result
          ? [
              { type: 'DataMaskingRun', id: 'LIST' },
              ...result.items.map((item) => ({ type: 'DataMaskingRun' as const, id: item.runId })),
            ]
          : [{ type: 'DataMaskingRun', id: 'LIST' }],
    }),
    getDataMaskingRun: builder.query<DataMaskingRunSummary, string>({
      query: (runId) => ({
        url: `/operations/data-masking/runs/${runId}`,
        method: 'GET',
      }),
      transformResponse: (response: BackendDataMaskingRunSummaryDto) =>
        transformDataMaskingRunSummary(response),
      providesTags: (_result, _error, runId) => [{ type: 'DataMaskingRun', id: runId }],
    }),
    getActiveDataMaskingRun: builder.query<DataMaskingActiveRun, void>({
      query: () => ({
        url: '/operations/data-masking/runs/active',
        method: 'GET',
      }),
      transformResponse: (response: BackendDataMaskingActiveRunDto) =>
        transformDataMaskingActiveRun(response),
      providesTags: [{ type: 'DataMaskingRun', id: 'ACTIVE' }],
    }),
  }),
});

export const {
  useGetDataMaskingRunsQuery,
  useGetDataMaskingRunQuery,
  useGetActiveDataMaskingRunQuery,
} = dataMaskingApi;
